import PendingWaitTime from "../../components/PendingWaitTime";
import { getBackendUrl as getMetaStageBackendUrl } from "../../config";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useHistory } from "react-router-dom";
import MetaCallTicketStageStatus from "../../components/MetaCallTicketStageStatus";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isMetaCallStageControlConfigured } from "../../services/metaCallStageControl";
import {
  Box,
  Avatar,
  TextField,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Switch,
  FormControl,
  Select,
  MenuItem
} from "@material-ui/core";

const api = axios.create({
  baseURL: getMetaStageBackendUrl(),
  timeout: 15000
});

const getHeaders = () => {
  const stored = localStorage.getItem("token");
  if (!stored) throw new Error("Sesion no disponible");

  return {
    Authorization: `Bearer ${JSON.parse(stored)}`
  };
};

// Control exclusivo para administradores.
// La API V14 verifica nuevamente el perfil en MySQL.

const fetchAllPages = async (path, key, params = {}) => {
  const results = [];

  for (let pageNumber = 1; pageNumber <= 100; pageNumber++) {
    const { data } = await api.get(path, {
      headers: getHeaders(),
      params: { ...params, pageNumber }
    });

    if (!Array.isArray(data[key])) {
      throw new Error("Respuesta inesperada del servidor");
    }

    results.push(...data[key]);

    if (!data.hasMore) return results;
  }

  throw new Error("Se alcanzo el limite de paginacion");
};

const AdminTicketsTest = () => {
  const historyPanelRef = useRef(null);
  const location = useLocation();
  const history = useHistory();
  const lastCallNavigation = useRef("");
  const [historyScrollVersion, setHistoryScrollVersion] = useState(0);
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  const [localAudioOpen, setLocalAudioOpen] = useState(false);
  const [riders, setRiders] = useState([]);
  const [riderChoices, setRiderChoices] = useState({});
  const [assigning, setAssigning] = useState(null);
  const [assignmentMessage, setAssignmentMessage] = useState("");
  const [assignmentError, setAssignmentError] = useState("");

  const matchesSearch = ticket => {
    const query = searchQuery.trim().toLocaleLowerCase();

    if (!query) return true;

    const contact = ticket.contact || {};

    const values = [
      contact.name,
      contact.number,
      ticket.id
    ];

    return values.some(value =>
      String(value ?? "").toLocaleLowerCase().includes(query)
    );
  };

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      if (!user || !["admin", "user"].includes(user.profile)) {
        throw new Error("Perfil no autorizado");
      }

      const { data: queues } = await api.get("/queue", {
        headers: getHeaders()
      });

      if (!Array.isArray(queues)) {
        throw new Error("Respuesta de colas no valida");
      }

      const queueIds = user.profile === "admin"
        ? queues.map(queue => queue.id)
        : Array.isArray(user.queues)
          ? user.queues.map(queue => queue.id)
          : [];

      const ticketParams = {
        queueIds: JSON.stringify(queueIds),
        ...(user.profile === "admin" ? { showAll: "true" } : {})
      };

      const [allTickets, allUsers] = await Promise.all([
        fetchAllPages("/tickets", "tickets", ticketParams),
        fetchAllPages("/users", "users")
      ]);

      setTickets(allTickets);
      setRiders(allUsers.filter(item => item.profile === "repartidor"));
    } catch (err) {
      setError("No fue posible cargar las conversaciones o los repartidores.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (historyScrollVersion > 0 && historyPanelRef.current) {
      historyPanelRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }, [historyScrollVersion]);

  const openHistory = async ticket => {
    setLocalAudioOpen(false);
    setSelected(ticket);
    setHistoryScrollVersion(v => v + 1);
    setMessages([]);
    setMessagesError("");
    setMessagesLoading(true);

    try {
      const { data } = await api.get(
        `/messages/${ticket.id}`,
        { headers: getHeaders() }
      );
      setMessages(data.messages || []);
    } catch (err) {
      setMessagesError(
        err.response?.data?.error ||
        "No fue posible consultar el historial."
      );
    } finally {
      setMessagesLoading(false);
    }
  };

  // META_CALL_TICKET_LINK_V1
  useEffect(() => {
    const requestedId = new URLSearchParams(location.search)
      .get("metaCallTicket");

    if (!requestedId) {
      lastCallNavigation.current = "";
      return;
    }

    if (!["localhost", "127.0.0.1"].includes(window.location.hostname) ||
        !isMetaCallStageControlConfigured() ||
        !/^[1-9][0-9]{0,14}$/.test(requestedId) ||
        loading) {
      return;
    }

    const key = `${user?.id || ""}:${requestedId}`;

    if (lastCallNavigation.current === key) {
      return;
    }

    lastCallNavigation.current = key;

    // Solo seleccionar conversaciones entregadas por la API.
    // El backend vuelve a comprobar acceso al consultar mensajes.
    const ticket = tickets.find(
      item => String(item.id) === requestedId
    );

    if (!ticket) {
      setError(
        "La conversacion solicitada no esta disponible en tu bandeja."
      );
      return;
    }

    setFilter(ticket.status);
    openHistory(ticket);
  }, [location.search, loading, tickets, user?.id]);
  const assignTicket = async ticket => {
    const riderId = Number(riderChoices[ticket.id]);
    const rider = riders.find(item => Number(item.id) === riderId);

    if (!riderId || !rider || assigning !== null) {
      setAssignmentError("Selecciona un repartidor disponible.");
      return;
    }

    if (!window.confirm(
      `¿Asignar la conversacion #${ticket.id} a ${rider.name}?`
    )) {
      return;
    }

    setAssigning(ticket.id);
    setAssignmentMessage("");
    setAssignmentError("");

    try {
      await api.post(
        `/tickets/${ticket.id}/assign`,
        { riderId },
        { headers: getHeaders() }
      );
      setAssignmentMessage(
        `Conversacion #${ticket.id} asignada a ${rider.name}.`
      );
      setRiderChoices(previous => ({
        ...previous,
        [ticket.id]: ""
      }));
    } catch (err) {
      setAssignmentError(
        err.response?.status === 409
          ? "La conversacion no esta disponible o el repartidor esta ocupado."
          : err.response?.data?.error ||
            "No fue posible asignar la conversacion."
      );
    } finally {
      setAssigning(null);
      await loadTickets();
    }
  };

  const availableRiders = riders;
  const filtered = tickets.filter(ticket => ticket.status === filter);

  return (
    <Box p={3} style={{ height: "100%", overflowY: "auto" }}>
      <Typography variant="h5">
        Bandeja administrativa
      </Typography>

      <Typography variant="body2" color="textSecondary">
        Conversaciones y asignacion manual de repartidores.
      </Typography>

      {/* Control experimental de historial desactivado */}

      <Box my={2}>
        {[
          ["pending", "Pendientes"],
          ["open", "Abiertas"],
          ["closed", "Cerradas"]
        ].map(([value, label]) => (
          <Button
            key={value}
            color="primary"
            variant={filter === value ? "contained" : "outlined"}
            onClick={() => {
              setFilter(value);
              setSelected(null);
              setMessages([]);
            }}
            style={{ marginRight: 8, marginBottom: 8 }}
          >
            {label} ({tickets.filter(t => t.status === value).length})
          </Button>
        ))}

        <Button onClick={loadTickets} disabled={loading}>
          Actualizar
        </Button>
      </Box>

      <Box mb={2}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          label="Buscar cliente"
          placeholder="Nombre, teléfono o número de conversación"
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
          inputProps={{ "aria-label": "Buscar conversaciones" }}
        />
      </Box>

      {loading && <CircularProgress size={24} />}

      {error && <Typography color="error">{error}</Typography>}

      {assignmentError && (
        <Typography color="error">{assignmentError}</Typography>
      )}

      {assignmentMessage && (
        <Typography style={{ color: "#237a37" }}>
          {assignmentMessage}
        </Typography>
      )}

      {!loading && !error && filtered.filter(matchesSearch).length === 0 && (
        <Typography color="textSecondary">
          No hay conversaciones en esta seccion.
        </Typography>
      )}

      {filtered.filter(matchesSearch).map(ticket => (
        <Paper
          key={ticket.id}
          variant="outlined"
          style={{ padding: 16, marginBottom: 10 }}
        >
          <Box
            display="flex"
            alignItems="center"
            mb={1}
          >
            <Avatar
              src={ticket.contact?.profilePicUrl || undefined}
              alt={ticket.contact?.name || "Contacto"}
              style={{
                width: 52,
                height: 52,
                marginRight: 12
              }}
            >
              {ticket.contact?.name?.trim()?.[0]?.toUpperCase() || "?"}
            </Avatar>

            <Box style={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                style={{
                  fontWeight: 600,
                  overflowWrap: "anywhere"
                }}
              >
                {ticket.contact?.name?.trim() ||
                  `Conversacion #${ticket.id}`}
              </Typography>

              <Typography
                variant="caption"
                color="textSecondary"
              >
                Conversacion #{ticket.id}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" color="textSecondary">
            Estado: {ticket.status} | Asignado a: {ticket.userId ?? "Nadie"}
          </Typography>
          <PendingWaitTime ticket={ticket} />

          {ticket.status === "pending" && ticket.userId == null && (
            <Box mt={2} mb={2}>
              <Typography variant="body2" color="textSecondary">
                Seleccionar repartidor
              </Typography>

              <Box mt={1} display="flex" alignItems="center" flexWrap="wrap">
                <FormControl
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 260, marginRight: 10, marginBottom: 8 }}
                >
                  <Select
                    displayEmpty
                    value={riderChoices[ticket.id] || ""}
                    onChange={event => {
                      const value = event.target.value;
                      setRiderChoices(previous => ({
                        ...previous,
                        [ticket.id]: value
                      }));
                    }}
                  >
                    <MenuItem value="">
                      Seleccionar repartidor
                    </MenuItem>
                    {availableRiders.map(rider => (
                      <MenuItem key={rider.id} value={rider.id}>
                        {rider.name} (#{rider.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  color="primary"
                  variant="contained"
                  disabled={
                    assigning !== null ||
                    !riderChoices[ticket.id] ||
                    availableRiders.length === 0
                  }
                  onClick={() => assignTicket(ticket)}
                  style={{ marginBottom: 8 }}
                >
                  {assigning === ticket.id
                    ? "Asignando..."
                    : "Asignar repartidor"}
                </Button>
              </Box>

              {availableRiders.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No hay repartidores disponibles.
                </Typography>
              )}
            </Box>
          )}

          <Box mt={1}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push(`/tickets/${ticket.id}`)}
              style={{ marginRight: 8 }}
            >
              Abrir chat y responder
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => openHistory(ticket)}
            >
              Ver mensajes recientes
            </Button>
          </Box>
        </Paper>
      ))}

      {selected && (
        <Paper ref={historyPanelRef} variant="outlined" style={{ padding: 16, marginTop: 18 }}>
          <Typography variant="h6">
            Historial de la conversacion #{selected.id}
          </Typography>

          {/* META_CALL_TICKET_STATUS_V1 */}
          {!messagesLoading &&
            !messagesError &&
            ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
            isMetaCallStageControlConfigured() &&
            new URLSearchParams(location.search).get("metaCallTicket") === String(selected.id) && (
              <MetaCallTicketStageStatus
                ticketId={selected.id}
                callId={new URLSearchParams(location.search).get("metaCallId")}
              />
            )}

                    {/* META_AUDIO_ADMIN_HISTORY_STAGING_V1 */}
          {selected.status === "open" &&
            !selected.isGroup &&
            !messagesLoading &&
            !messagesError &&
            user &&
            ["admin", "user"].includes(user.profile) &&
            ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
            isMetaCallStageControlConfigured() && (
              <Box
                mb={2}
                p={2}
                style={{
                  border: "1px solid #dddddd",
                  borderRadius: 8
                }}
              >
                <Typography variant="subtitle1">
                  Diagnostico de audio - staging
                </Typography>

                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginBottom: 10 }}
                >
                  Prueba local de microfono y altavoz.
                  No conecta llamadas ni transmite audio a Meta.
                </Typography>

                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  aria-expanded={localAudioOpen}
                  onClick={() => setLocalAudioOpen(previous => !previous)}
                >
                  {localAudioOpen
                    ? "Cerrar diagnostico de audio"
                    : "Abrir diagnostico de audio"}
                </Button>

                {localAudioOpen && (
                  <iframe
                    title="Diagnostico local de audio MandaExpress"
                    src="/meta-audio-test.html"
                    allow="microphone; autoplay"
                    style={{
                      display: "block",
                      width: "100%",
                      height: 470,
                      marginTop: 12,
                      border: "1px solid #dddddd",
                      borderRadius: 8,
                      backgroundColor: "#ffffff"
                    }}
                  />
                )}
              </Box>
            )}

          <Divider style={{ margin: "12px 0" }} />

          {messagesLoading && <CircularProgress size={22} />}

          {messagesError && (
            <Typography color="error">{messagesError}</Typography>
          )}

          {!messagesLoading && !messagesError &&
            messages.length === 0 && (
              <Typography color="textSecondary">
                Esta conversacion no tiene mensajes.
              </Typography>
            )}

          {messages.map(message => (
            <Box key={message.id} mb={2}>
              <Typography variant="body2" color="textSecondary">
                {message.fromMe ? "Enviado" : "Recibido"}
              </Typography>

              <Typography style={{ whiteSpace: "pre-wrap" }}>
                {message.body}
              </Typography>

              <Divider style={{ marginTop: 10 }} />
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default AdminTicketsTest;
